/**
 * Pure zoom/pan transform math for the map viewport (Big Phase 1.5 UI).
 *
 * The viewport applies a screen-space affine transform over the SVG content:
 *
 *     screen = scale * content + (tx, ty)
 *
 * where `content` is a point in the SVG's user space (the Y-down map coords the
 * {@link MapCanvas} already draws in). Keeping the math PURE here makes the
 * zoom-to-cursor + pan-clamp + fit behavior unit-testable without a DOM; the
 * React wrapper ({@link MapViewport}) just feeds it wheel/pointer deltas and
 * renders the resulting `transform` string.
 */

/** The viewport transform state: a uniform scale + a screen-space translation. */
export interface ViewportState {
  readonly scale: number;
  readonly tx: number;
  readonly ty: number;
}

/** A screen-space point (pixels, origin top-left of the viewport element). */
export interface Point {
  readonly x: number;
  readonly y: number;
}

/** A size in pixels. */
export interface Size {
  readonly width: number;
  readonly height: number;
}

/** An axis-aligned box in CONTENT (SVG user) space. */
export interface Box {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/** The zoom range: 1× = fit, up to MAX_SCALE× in; cannot zoom out past fit. */
export const MIN_SCALE = 1;
export const MAX_SCALE = 8;

/** The identity viewport (no zoom, no pan). */
export const IDENTITY: ViewportState = { scale: 1, tx: 0, ty: 0 };

/** Clamp a scale into [MIN_SCALE, MAX_SCALE]. */
export function clampScale(scale: number): number {
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale));
}

/**
 * Zoom by `factor` about a fixed screen `cursor` point so the CONTENT point
 * under the cursor stays under the cursor after the zoom (the standard
 * zoom-to-cursor). Scale is clamped to [MIN_SCALE, MAX_SCALE]; when the clamp
 * caps the scale the translation uses the ACTUAL applied factor so the invariant
 * still holds at the limit.
 *
 * Derivation: with `screen = s·c + t`, the content point under the cursor is
 * `(cursor − t)/s`. Requiring it to map to the same `cursor` after `s' = s·f`
 * gives `t' = cursor − f·(cursor − t)`.
 */
export function zoomAbout(state: ViewportState, factor: number, cursor: Point): ViewportState {
  const nextScale = clampScale(state.scale * factor);
  const appliedFactor = nextScale / state.scale; // the real factor after clamping
  return {
    scale: nextScale,
    tx: cursor.x - appliedFactor * (cursor.x - state.tx),
    ty: cursor.y - appliedFactor * (cursor.y - state.ty),
  };
}

/** Pan by a screen-space delta. */
export function panBy(state: ViewportState, dx: number, dy: number): ViewportState {
  return { ...state, tx: state.tx + dx, ty: state.ty + dy };
}

/**
 * Clamp the pan so the content cannot be dragged entirely out of the viewport.
 * At scale `s` the content occupies `s · viewport` in screen space (the content
 * is pre-fit to the viewport at scale 1); we keep at least the viewport's own
 * extent covered, so `tx ∈ [viewport.w·(1−s), 0]` (and likewise y). At scale 1
 * the only valid translation is 0 (fully fit, no pan), which also recenters.
 */
export function clampPan(state: ViewportState, viewport: Size): ViewportState {
  const minTx = viewport.width * (1 - state.scale);
  const minTy = viewport.height * (1 - state.scale);
  return {
    scale: state.scale,
    tx: Math.min(0, Math.max(minTx, state.tx)),
    ty: Math.min(0, Math.max(minTy, state.ty)),
  };
}

/**
 * The combined wheel-zoom step: zoom about the cursor by `factor`, then re-clamp
 * the pan so a zoom-out cannot leave the content detached from the viewport.
 */
export function wheelZoom(state: ViewportState, factor: number, cursor: Point, viewport: Size): ViewportState {
  return clampPan(zoomAbout(state, factor, cursor), viewport);
}

/** The combined drag-pan step: pan by the delta, then clamp. */
export function dragPan(state: ViewportState, dx: number, dy: number, viewport: Size): ViewportState {
  return clampPan(panBy(state, dx, dy), viewport);
}

/**
 * Reset to "fit": scale 1, no translation. Because {@link MapCanvas} already
 * fits the content to its own `viewBox` (`preserveAspectRatio`), the viewport's
 * fit is simply the identity transform — the content is shown whole, centered.
 * (The SVG viewBox does the content→viewport letterboxing; the viewport
 * transform layers zoom/pan ON TOP in screen space.)
 */
export function fit(): ViewportState {
  return IDENTITY;
}

/** Serialize a viewport state to the SVG `transform` attribute string. */
export function toTransform(state: ViewportState): string {
  return `translate(${round(state.tx)} ${round(state.ty)}) scale(${round(state.scale)})`;
}

/** The content point currently under a screen point (inverse transform) — for tests/hit-testing. */
export function contentUnder(state: ViewportState, screen: Point): Point {
  return { x: (screen.x - state.tx) / state.scale, y: (screen.y - state.ty) / state.scale };
}

function round(n: number): number {
  // Keep the transform string stable + compact (sub-pixel precision is noise).
  return Math.round(n * 1000) / 1000;
}
